import { Link, useSearchParams } from 'react-router-dom';
import { GovBar } from '@/features/common/layout/GovBar';
import { SITE_PAGE } from '@/features/common/layout/publicPageLinks';
import { useDocumentTitle } from '@/features/common/useDocumentTitle';
import { auth } from '@/i18n/dictionaries/auth';
import { publicLayout } from '@/i18n/dictionaries/publicLayout';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/auth/auth.css';

/**
 * /auth/mpass-error — the screen a failed or cancelled MPass sign-in lands on
 * (akis-mpass-integration §7.6).
 *
 * Replaces `auth/mpass-error.html`. Two callers redirect here, both server-side:
 * MPassSamlConfig's SAML failureHandler and MockAuthController's blocked-account branch. Both
 * carry the account status as `reason`, and AuthErrorController forwards rather than redirects
 * precisely so that query string survives to here.
 *
 * `reason` decides between two states and is never rendered, which is what kept the original free
 * of reflected content; reading it through useSearchParams and comparing it keeps that true.
 */
export function MpassErrorPage() {
  const { t } = useTranslation(auth);
  // The gov-bar's own two links belong to the chrome namespace, not this page's.
  const { t: tLayout } = useTranslation(publicLayout);
  const [params] = useSearchParams();
  const reason = params.get('reason');

  // Same predicate the controller used to compute `accountBlocked`.
  const blocked = reason === 'DEACTIVATED' || reason === 'DELETED';

  useDocumentTitle(t('errorTitle'));

  return (
    <>
      {/* Unlike the mock sign-in screen, this one keeps the gov-bar: the visitor is still on
          AKIS — the redirect landed back here after MPass refused them. `contained` and both
          links match what the template's gov-bar fragment rendered. */}
      <GovBar
        contained
        links={
          <>
            <Link to={SITE_PAGE.accessibility}>{tLayout('govbarAccessibility')}</Link>
            <Link to="/sitemap">{tLayout('navSitemap')}</Link>
          </>
        }
      />
      <div className="mpass-panel">
        <div className="mpass-panel__card card">
          <div className="mpass-panel__icon" aria-hidden="true">
            ⚠
          </div>
          <h1>{blocked ? t('blockedHeading') : t('failedHeading')}</h1>
          <p>{blocked ? t('blockedBody') : t('failedBody')}</p>
          {blocked ? (
            <Link className="btn btn--primary" to="/contact">
              {t('blockedCta')}
            </Link>
          ) : (
            // A real navigation, not a router link: this restarts the server-side redirect
            // chain out to the identity provider.
            <a className="btn btn--primary" href="/saml/authenticate/mpass">
              {t('failedCta')}
            </a>
          )}{' '}
          <Link className="btn btn--ghost" to="/">
            {t('home')}
          </Link>
        </div>
      </div>
    </>
  );
}
